import { Button, Input, Space } from 'antd';
import { cloneDeep } from 'lodash';
import React from 'react';

import Icon from '@ant-design/icons';
import PlusIcon from '@icons/emp/plus_icon.svg';
import TreeIcon from '@icons/sankey/tree-icon.svg';
import { IEventJourney } from '@src/emp-3.0/components/Sankey/Configurations/EventJourney/typed';
import {
  CompactDualSelectWithRequest,
  DeleteIcon,
  dimensionValueLabelRender,
} from '@src/emp-3.0/components/Sankey/components/CompactDualSelect';
import { DimensionValueSelect } from '@src/emp-3.0/components/Sankey/components/CompactDualSelect/styled';
import DimensionRemoveConfirmation from '@src/emp-3.0/components/Sankey/components/DimensionRemoveConfirmation';
import {
  isEventBreakdown,
  isLandingEvent,
  isSubsetArray,
} from '@src/emp-3.0/components/Sankey/utils/check';
import { IEventBreakdown, IEventFilter } from '@src/emp-3.0/types';
import { toLowerCase } from '@src/emp-3.0/utils';
import { addEventNameToFilters } from '@src/emp-3.0/components/Sankey/utils';

const EventBreakDown: React.FC<IEventJourney> = ({
  eventData,
  showDimensionTitle = false,
  filters,
  interval,
  showDelete = true,
  loading,
  eventOptions = [],
  fixEvent = [],
  compactDualSelectProps = {},
  onDeleteEvent,
  onDimensionChange,
  onEventNameChange,
  onEventChange,
  onAddDimension,
  onDeleteDimension,
  addDimensionButtonProps = { show: true, text: 'Add Dimension' },
}) => {
  const {
    dimensionSelectProps,
    dimensionValueSelectProps,
    style: compactDualSelectStyle,
    onDelete,
    ...restCompactDualSelectProps
  } = compactDualSelectProps;

  const eventFilters: IEventFilter[][] = eventData?.breakdowns || [];

  const isFixBreakdownEvent = fixEvent.length > 0 && isEventBreakdown(fixEvent[0]);

  const dimensionNameOptions = dimensionSelectProps?.options?.map((item) => {
    if (eventFilters.some((andFilter) => andFilter[0].field === item.value)) {
      return {
        ...item,
        disabled: true,
      };
    }
    return item;
  });

  const handleEventChange = (value) => {
    onEventChange?.(value);
    onEventNameChange?.(value);
    const cloneEventData = cloneDeep(eventData);
    cloneEventData.event_name = value;
    if (isLandingEvent(cloneEventData)) {
      cloneEventData.filters = [];
    }
  };

  const handleChange = (value, andIndex) => {
    const cloneEventData: IEventBreakdown = cloneDeep(eventData);
    cloneEventData.breakdowns[andIndex] = value;
    onDelete?.();
    onDimensionChange?.(cloneEventData.breakdowns);
  };

  const handleDelete = (value, removeAndIndex) => {
    onDeleteDimension?.(value, removeAndIndex);
    const cloneEventData: IEventBreakdown = cloneDeep(eventData);

    cloneEventData.breakdowns = cloneEventData.breakdowns.filter(
      (_, index) => index !== removeAndIndex,
    );
    onDimensionChange?.(cloneEventData.breakdowns);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          style={{
            width: 64,
            background: '#e6edff',
            color: 'rgba(0,0,0,0.88)',
            cursor: 'default',
            borderRadius: '2px',
            flexShrink: 0,
          }}
          value="Event: "
          disabled
        />
        <DimensionValueSelect
          loading={loading}
          value={eventData.event_name}
          optionLabelProp="value"
          options={eventOptions}
          filterOption={(inputValue, option) =>
            toLowerCase(option?.value ?? '').includes(toLowerCase(inputValue))
          }
          showSearch
          showRightBorder={!showDelete}
          disabled={isFixBreakdownEvent && !showDelete}
          onChange={(value) => {
            handleEventChange(value);
          }}
        />
        {showDelete && <DeleteIcon onClick={onDeleteEvent} />}
      </Space.Compact>
      {showDimensionTitle && eventFilters.length > 0 && (
        <span
          style={{
            color: 'rgba(0, 0, 0, 0.4)',
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '-0.12px',
            padding: '0 8px',
          }}
        >
          Breakdown Dimension :
        </span>
      )}
      {eventFilters.map((andFilters, andIndex) => {
        const field = andFilters[0].field;
        const value = andFilters[0].value;

        const findEvent = fixEvent.find((event) => event.event_name === eventData.event_name);
        const isFixBreakdownEvent = findEvent && isEventBreakdown(findEvent);

        const notShowDelete =
          isEventBreakdown(eventData) &&
          isFixBreakdownEvent &&
          findEvent.breakdowns.some(
            (andItem) => field === andItem[0].field && isSubsetArray(andItem[0].value, value),
          );

        let disabledValueOptions: string[] = [];
        isFixBreakdownEvent &&
          findEvent.breakdowns.forEach((andItem) => {
            if (andItem[0].field === field) {
              disabledValueOptions = andItem[0].value;
            }
          });
        const filtersWithBreakdownEvent = addEventNameToFilters(filters, eventData.event_name);

        return (
          <CompactDualSelectWithRequest
            key={`${field}_${andIndex}`}
            filters={filtersWithBreakdownEvent}
            interval={interval}
            disabledValueOptions={disabledValueOptions}
            showDelete={!notShowDelete}
            dimensionSelectProps={{
              value: field,
              ...dimensionSelectProps,
              disabled: notShowDelete || dimensionSelectProps?.disabled,
              showArrow: !notShowDelete || dimensionSelectProps?.showArrow,
              options: dimensionNameOptions,
              onChange: (value, ...rest) => {
                handleChange([{ ...andFilters[0], field: value, value: [] }], andIndex);
                dimensionSelectProps?.onChange?.(value, ...rest);
              },
              style: { minWidth: 80, ...dimensionSelectProps?.style },
            }}
            dimensionValueSelectProps={{
              value: value,
              ...dimensionValueSelectProps,
              optionLabelProp: 'value',
              labelRender: dimensionValueLabelRender,
              onChange: (value: undefined | string | string[], ...rest) => {
                if (typeof value === 'string') {
                  handleChange([{ ...andFilters[0], value: [value] }], andIndex);
                  dimensionValueSelectProps?.onChange?.([value], ...rest);
                } else {
                  handleChange([{ ...andFilters[0], value: value ?? [] }], andIndex);
                  dimensionValueSelectProps?.onChange?.(value, ...rest);
                }
              },
              style: { ...dimensionValueSelectProps?.style },
            }}
            style={{ paddingLeft: 16, ...compactDualSelectStyle }}
            prefix={
              <Icon
                component={TreeIcon}
                style={{
                  backgroundColor: '#f5f5f5',
                  fontSize: '10px',
                  border: '1px solid #d9d9d9',
                  borderRight: 0,
                  paddingLeft: '8px',
                  display: 'inline-flex',
                }}
              />
            }
            // onDelete={() => handleDelete(andFilters, andIndex)}
            deleteRender={(deleteNode) => (
              <DimensionRemoveConfirmation onConfirm={() => handleDelete(andFilters, andIndex)}>
                {deleteNode}
              </DimensionRemoveConfirmation>
            )}
            {...restCompactDualSelectProps}
          />
        );
      })}
      {addDimensionButtonProps.show && (
        <Button
          type="link"
          disabled={!eventData?.event_name}
          onClick={onAddDimension}
          style={{ width: 154, paddingInlineStart: 0 }}
        >
          <Icon component={PlusIcon} />
          {addDimensionButtonProps?.text ?? 'Add Dimension'}
        </Button>
      )}
    </div>
  );
};

export default EventBreakDown;
